import { useEffect, useState } from 'react';
import dayjs, { type Dayjs } from 'dayjs';
import { NotificationTextMessage } from '@/core/enums/notification';
import { toIsoDate, type PeriodRow } from '@/core/models/practice.models';
import { notifyError } from '@/core/notify/notify';
import { dateRangeProblem } from '@/core/utils/dates';
import { filingService } from '@/core/services/filing';
import { practiceService } from '@/core/services/practice';
import { Button } from '@/kit/Button';
import { DateField, type DateValue } from '@/kit/DateField';
import { TextareaField } from '@/kit/FormField';
import { Icon } from '@/kit/Icon';
import { RequiredMark } from '@/kit/RequiredMark';
import { Select, SelectOption } from '@/kit/Select';
import { DialogClose, DialogContent, type DialogComponentProps } from '@/kit/dialog';

export interface SubmitAccountsDialogData {
  clientId: string;
}

const maxFileBytes = 10 * 1024 * 1024;

/**
 * "Submit Imported iXBRL Accounts": attaches an iXBRL file to a new submission for an accounting period. Closes with
 * true once imported, false when there is no client, undefined on Cancel (the parent toasts the success).
 */
export function SubmitAccounts({ data, dialogRef }: DialogComponentProps<SubmitAccountsDialogData, boolean>) {
  const clientId = data?.clientId;

  const [periods, setPeriods] = useState<PeriodRow[]>([]);
  const [description, setDescription] = useState('');
  const [periodId, setPeriodId] = useState<string | null>(null);
  const [fromDateValue, setFromDateValue] = useState<DateValue>(null);
  const [toDateValue, setToDateValue] = useState<DateValue>(null);
  const [signDateValue, setSignDateValue] = useState<DateValue>(null);
  const [file, setFile] = useState<File | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!clientId) {
      notifyError('Select a client from Accounts first.');
      dialogRef.close(false);
      return;
    }
    practiceService.getClientPeriods(clientId).then(
      (response) => setPeriods(response.periods),
      (error) => notifyError(error),
    );
    // Runs once, when the dialog opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onPeriodChange = (id: string | null) => {
    setPeriodId(id);
    const period = periods.find((p) => p.id === id);
    setFromDateValue(period?.periodFrom ?? null);
    setToDateValue(period?.periodTo ?? null);
  };

  const onFileSelected = (files: FileList | null) => {
    if (!files || files.length === 0) {
      return;
    }
    if (files.length > 1) {
      notifyError('Choose one iXBRL accounts file.');
      return;
    }
    const selected = files[0];
    if (!/\.(html|htm|xhtml)$/i.test(selected.name)) {
      notifyError('Please upload the iXBRL accounts as an .html, .htm or .xhtml file.');
      return;
    }
    if (selected.size > maxFileBytes) {
      notifyError(NotificationTextMessage.fileSizeExceeds);
      return;
    }
    setFile(selected);
  };

  const save = () => {
    if (!clientId || isSaving) {
      return;
    }
    if (!periodId) {
      notifyError('Select the accounting period.');
      return;
    }
    const signDate = toIsoDate(signDateValue);
    if (!signDate) {
      notifyError("Enter the directors' signing date.");
      return;
    }
    if (!file) {
      notifyError(NotificationTextMessage.nofileError);
      return;
    }
    // The directors sign on or after the day the period ends, and the period itself must run forwards.
    const rangeProblem = dateRangeProblem(fromDateValue, toDateValue, 'The period');
    if (rangeProblem) {
      notifyError(rangeProblem);
      return;
    }
    if (toDateValue && signDate < (toIsoDate(toDateValue) ?? '')) {
      notifyError('The signing date cannot be before the end of the period.');
      return;
    }

    setIsSaving(true);
    filingService
      .importIxbrl({
        clientId,
        accountingPeriodId: periodId,
        description: description.trim() || null,
        fromDate: toIsoDate(fromDateValue),
        toDate: toIsoDate(toDateValue),
        signDate,
        file,
      })
      .then(
        () => dialogRef.close(true),
        (error) => notifyError(error),
      )
      .finally(() => setIsSaving(false));
  };

  return (
    <div className="mat-dialog-wrapper">
      <DialogContent>
        <form onSubmit={(event) => event.preventDefault()}>
          <div className="main-contain">
            <div className="sidenav-header">
              <div className="d-flex align-items-center justify-space-between">
                <h2 className="m-0">
                  <span>Submit iXBRL Accounts</span>
                </h2>
                <DialogClose>
                  <Icon name="close" className="close" />
                </DialogClose>
              </div>
            </div>
          </div>
          <div className="sidenav-body">
            <div className="form-field textarea">
              <p>Description</p>
              <TextareaField
                fieldClassName="w-387"
                maxLength={500}
                name="description"
                value={description}
                onChange={(event) => setDescription(event.target.value)}
              />
            </div>
            <div className="form-field textarea">
              <p>
                Period<span className="text-red">*</span>
              </p>
              <Select
                className="w-387"
                name="periodId"
                placeholder="--Select Period--"
                value={periodId}
                onChange={(value) => onPeriodChange((value as string | null) ?? null)}
              >
                {periods.map((period) => (
                  <SelectOption key={period.id} value={period.id}>
                    {`${period.fromDate} to ${period.toDate}`}
                  </SelectOption>
                ))}
              </Select>
            </div>
            <div className="d-flex gap-30">
              <div className="form-field">
                <p>Start Date</p>
                <DateField
                  fieldClassName="w-180"
                  readOnly
                  name="fromDate"
                  value={fromDateValue}
                  onChange={(value: Dayjs | null) => setFromDateValue(value)}
                />
              </div>
              <div className="form-field">
                <p>End Date</p>
                <DateField
                  fieldClassName="w-180"
                  readOnly
                  name="toDate"
                  value={toDateValue}
                  onChange={(value: Dayjs | null) => setToDateValue(value)}
                />
              </div>
            </div>
            <div className="d-flex gap-30">
              <div className="form-field">
                <p>
                  Sign Date <span className="text-red">*</span>
                </p>
                <DateField
                  fieldClassName="w-180"
                  readOnly
                  name="signDate"
                  min={toDateValue ? dayjs(toIsoDate(toDateValue)) : undefined}
                  value={signDateValue}
                  onChange={(value: Dayjs | null) => setSignDateValue(value)}
                />
              </div>
            </div>
            <div className="form-field attached">
              <p>
                Attachment <RequiredMark />
              </p>
              <div className="attachFile" style={{ maxWidth: '100%' }}>
                <Icon name="cloud_upload" outlined />
                <input
                  multiple
                  type="file"
                  autoComplete="off"
                  maxLength={100}
                  id="file"
                  accept=".html,.htm,.xhtml"
                  onChange={(event) => {
                    onFileSelected(event.target.files);
                    // so the same file can be picked again
                    event.target.value = '';
                  }}
                />
                <label htmlFor="file">
                  <strong>{file ? file.name + ' ' : 'Choose a file '}</strong>
                  <span className="dragndrop">/ Drag it here</span>.
                </label>
              </div>
            </div>
            <div className="info-message align-items-start">
              <Icon name="info" className="material-icons-outlined" />
              <ul>
                <li>
                  Selected &apos;Accounting Period&apos; must be identical to the accounting period mentioned in
                  imported iXBRL report.
                </li>
                <li>
                  &apos;Directors Signing Date&apos; mentioned above must be identical to the &apos;Directors Signing
                  Date&apos; mentioned in your iXBRL report.
                </li>
              </ul>
            </div>
          </div>

          <div className="d-flex gap-15 hr-top pt-1 mt-1">
            <Button className="action-button primary-button" busy={isSaving} onClick={save}>
              Save
            </Button>
            <DialogClose as="button" type="button" className="action-button secondary-button">
              Cancel
            </DialogClose>
          </div>
        </form>
      </DialogContent>
    </div>
  );
}
